/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import {
  useLiveAngleVideoUrl,
  useLiveCameraVideoUrl,
} from "components/scrapEyeVideoPlayer/hooks";
import { useTenantTranslation } from "hooks/formatters";
import { EnsureGcsTokenExists } from "components/scrapEyeVideoPlayer/GcsTokenProvider";
import { SynchronizedHlsVideo } from "./SynchronizedHlsVideo";

type LiveAngleStreamPlayerProps = Readonly<{
  location: string;
  angle: string;
  videoIndex?: number;
  deviceId?: number;
}>;

export const LiveStreamPlayer = ({
  location,
  angle,
  videoIndex = 0,
  deviceId,
}: LiveAngleStreamPlayerProps) => {
  const { t } = useTenantTranslation();

  const {
    url: hlsUrl,
    isLoading,
    error,
  } = useLiveAngleVideoUrl(location, angle, { segmentCount: 12, deviceId });

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "2px dashed",
          borderColor: "grey.300",
          borderRadius: 2,
          height: 400,
          width: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{t("videoStreamError")}</Alert>;
  }

  if (!hlsUrl) {
    return null;
  }

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <EnsureGcsTokenExists>
        <SynchronizedHlsVideo
          srcUrl={hlsUrl}
          videoId={`live-angle-${location}-${angle}-${videoIndex}`}
          videoIndex={videoIndex}
          isLive
        />
      </EnsureGcsTokenExists>
      <Box
        sx={{
          position: "absolute",
          top: 8,
          left: 8,
          bgcolor: "rgba(0, 0, 0, 0.7)",
          color: "white",
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          backdropFilter: "blur(4px)",
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {angle}
        </Typography>
      </Box>
    </Box>
  );
};

type LiveCameraStreamPlayerProps = Readonly<{
  deviceId: number;
  physicalId: string;
  videoIndex?: number;
}>;

export const LiveCameraStreamPlayer = ({
  deviceId,
  physicalId,
  videoIndex = 0,
}: LiveCameraStreamPlayerProps) => {
  const { t } = useTenantTranslation();

  const {
    url: hlsUrl,
    isLoading,
    error,
  } = useLiveCameraVideoUrl(deviceId, physicalId, { segmentCount: 12 });

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "2px dashed",
          borderColor: "grey.300",
          borderRadius: 2,
          height: 400,
          width: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{t("videoStreamError")}</Alert>;
  }

  if (!hlsUrl) {
    return null;
  }

  return (
    <Box sx={{ position: "relative", width: "100%" }}>
      <EnsureGcsTokenExists>
        <SynchronizedHlsVideo
          srcUrl={hlsUrl}
          videoId={`live-camera-${deviceId}-${physicalId}-${videoIndex}`}
          videoIndex={videoIndex}
          isLive
        />
      </EnsureGcsTokenExists>
      <Box
        sx={{
          position: "absolute",
          top: 8,
          left: 8,
          bgcolor: "rgba(0, 0, 0, 0.7)",
          color: "white",
          px: 1.5,
          py: 0.5,
          borderRadius: 1,
          backdropFilter: "blur(4px)",
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          {physicalId}
        </Typography>
      </Box>
    </Box>
  );
};
